import {
  Navigate,
  Route,
  RouterProvider,
  createBrowserRouter,
  createRoutesFromElements,
} from "react-router-dom"
import { PrivateRoute } from "@app/components"
import { ErrorBoundary } from "@core/components"
import { CssBaseline, ThemeProvider } from "@mui/material"
import { LoginPage, LogoutPage, OIDCCallbackPage } from "@app/pages"
import {
  useAppSelector,
  useAppTheme,
  useDefaultAppThemeName,
  useFirstAvailablePage,
} from "@app/hooks"
import { getAppThemeName } from "@app/store"
import { App as AppInterface } from "@app/interfaces"
import { LicenseInfo } from "@mui/x-data-grid-pro"
import { LocalizationProvider } from "@mui/x-date-pickers"
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns"
import { ru } from "date-fns/locale"
import { ErrorPage } from "@core/pages"
import { LinkComparePage } from "./modules/tgstat/pages"
import {
  KrakenAnalyticsPage,
  LigaPage,
  RealizationPage,
} from "./modules/marketplace/pages"
import {
  MarketplaceCitiesTable,
  MarketplaceDistrictsTable,
  MarketplaceProductsTable,
} from "./modules/marketplace/components"
import { ProductCategoriesTable } from "./modules/marketplace/components/productCategories"
import { ProductPaymentCategorByProduct } from "./modules/marketplace/components/measure/productPaymentCategorByProduct"
import { HrCandidatePage } from "./modules/hr/pages"
import { StaffTable } from "./modules/marketplace/components/staffTable"
import { QuestionnaireTable } from "./modules/marketplace/components/questionnaireTable"
import { MetabaseEmbed } from "./modules/pr/components"
import {
  BotMonitorPage,
  NcMembersPage,
  TelegramPage,
} from "./modules/telegram/pages"
import { Metabase } from "./modules/pr/interfaces"
import { ProcurementsTable } from "./modules/marketplace/components/procurementsTable"
import {
  CreateMailingPage,
  MailingsPage as TgMailingsPage,
} from "./modules/telegram/pages"
import { ChatViewerPage } from "./modules/telegram/chat_viewer/pages"
import { ChatAnalyzePage } from "./modules/telegram/chat_analyze/pages"
import {
  GuestBotsPage,
  GuestBotPage,
  TemplatesPage,
  GuestBotLogsPage,
  BindingsPage,
  ChatsPage,
  JoinJobProgressPage,
  MailingProgressPage,
} from "./modules/telegramGuestBot/pages"
import {
  OrdersPage,
  OrderPage,
  AgentsPage,
  TopUpPage,
  PaymentsPage,
  ContentsPage,
} from "./modules/adv2/pages"
import { AgentPage } from "./modules/adv2/pages/agentPage"
import { PrivateLayout, PublicLayout } from "./modules/app/layout"
import { generateLicense } from "@mui/x-license-pro/generateLicense"
import { InviteLinkPage } from "./modules/tgstatBot/pages/inviteLInkPage"
import { MyChatsPage } from "./modules/tgstatBot/pages/myChatsPage"
import { ChatPage } from "./modules/tgstatBot/pages/chatPage"
import { postBotRoutes } from "./modules/postBot/pages/postBotRoutes"
import "./modules/postBot/services/publicationSessionLifecycle"
import { AnalyticsPage } from "./modules/tgstatBot/pages/analyticsPage"
import { OIDCProvider } from "./modules/app/components/auth/oidc"
import { SherlokPage } from "./modules/osint/pages"
import {
  ProxiesPage,
  RolesPage,
  AccountsPage,
  ChainBuilderPage,
  ChainsPage,
  FinishedChainReportPage,
  ChainReportResolverPage,
  ScheduledChainsPage,
  ScheduledRunsPage,
  TriggersPage,
  MembershipsPage,
  MassOpsPage,
  TemplatesGalleryPage,
  TemplateRunPage,
  TemplateEditorPage,
  BatchesPage,
  BatchProgressPage,
  ProfileDictionariesPage,
  JoinRequestsPage,
  JoinRecoveryPage,
  DialogsPage,
  DialogPassesPage,
  MembershipClarificationsPage,
} from "./modules/userbot/pages"
import { SchedulePage, ShiftTypesPage } from "./modules/staffSchedule/pages"

/**
 * Редирект на первую доступную пользователю страницу.
 * Предотвращает бесконечный цикл, если дефолтный маршрут недоступен.
 */
function DefaultRedirect() {
  const firstPage = useFirstAvailablePage()

  if (!firstPage) {
    return <Navigate to="/logout" replace />
  }

  return <Navigate to={firstPage} replace />
}

const expiryDate = new Date()
expiryDate.setFullYear(new Date().getFullYear() + 1)
const licenseKey = generateLicense({
  orderNumber: "2658349",
  expiryDate: expiryDate,
  scope: "premium",
  licensingModel: "subscription",
})
LicenseInfo.setLicenseKey(licenseKey)

const getRouter = () =>
  createBrowserRouter(
    createRoutesFromElements(
      <>
        <Route path="/" element={<PublicLayout />}>
          {/* anonimous */}
          <Route
            element={
              <PrivateRoute
                needRole={AppInterface.UserRole.ANON}
                exactRole
                redirectTo={"/"}
              />
            }
          >
            <Route index path="login" element={<LoginPage />} />
            <Route path="auth/callback" element={<OIDCCallbackPage />} />
          </Route>
        </Route>

        {/* not anonimous */}
        <Route path="/" element={<PrivateLayout />}>
          <Route
            path="logout"
            element={
              <PrivateRoute
                element={<LogoutPage />}
                needRole={AppInterface.UserRole.USER}
                redirectTo={"/login"}
              />
            }
          />

          {/* Default redirect — на первую доступную пользователю страницу */}
          <Route index element={<DefaultRedirect />} />

          {/* Telegram Routes */}
          <Route path="telegram">
            <Route
              path="bot-monitor"
              element={
                <PrivateRoute
                  needRole={AppInterface.UserRole.MANAGER}
                  accessPolicy={AppInterface.AccessPolicy.BOT_MONITOR_READ}
                  element={<BotMonitorPage />}
                />
              }
            />
            {/* Chats - Accessible to USER with TG_INVITE_LINKS module */}
            <Route
              path="chats"
              element={
                <PrivateRoute
                  needRole={AppInterface.UserRole.USER}
                  needModule={AppInterface.ModuleName.TG_INVITE_LINKS}
                  redirectTo={"/login"}
                  enableComeBackRedirect
                />
              }
            >
              <Route index element={<MyChatsPage />} />
              <Route path=":botId/:chatId">
                <Route index element={<ChatPage />} />
                <Route path="link" element={<InviteLinkPage />} />
              </Route>
            </Route>

            {/* Chat Viewer - Accessible to USER with TG_BOT_CHAT module */}
            <Route
              path="chat-viewer"
              element={
                <PrivateRoute
                  needRole={AppInterface.UserRole.USER}
                  needModule={AppInterface.ModuleName.TG_BOT_CHAT}
                  redirectTo={"/login"}
                  enableComeBackRedirect
                  element={<ChatViewerPage />}
                />
              }
            />

            {/* Chat Analyze - Manager + TG_CHAT_ANALYZE */}
            <Route
              path="chat-analyze"
              element={
                <PrivateRoute
                  needRole={AppInterface.UserRole.MANAGER}
                  needModule={AppInterface.ModuleName.TG_CHAT_ANALYZE}
                  redirectTo={"/login"}
                  enableComeBackRedirect
                  element={<ChatAnalyzePage />}
                />
              }
            />

            {/* NC Updates - Manager + NC */}
            <Route
              path="nc-updates"
              element={
                <PrivateRoute
                  needRole={AppInterface.UserRole.MANAGER}
                  needModule={AppInterface.ModuleName.NC}
                  redirectTo={"/login"}
                  element={<TelegramPage />}
                />
              }
            />

            {/* NC Members - Manager + NC */}
            <Route
              path="nc-members"
              element={
                <PrivateRoute
                  needRole={AppInterface.UserRole.MANAGER}
                  needModule={AppInterface.ModuleName.NC}
                  redirectTo={"/login"}
                  element={<NcMembersPage />}
                />
              }
            />

            {/* Mailings - Manager + TG_MAILING */}
            <Route
              path="mailings"
              element={
                <PrivateRoute
                  needRole={AppInterface.UserRole.MANAGER}
                  needModule={AppInterface.ModuleName.TG_MAILING}
                  redirectTo={"/login"}
                />
              }
            >
              <Route path="new" element={<CreateMailingPage />} />
              <Route path="list" element={<TgMailingsPage />} />
            </Route>

            {/* Guest Bots - Manager + TG_MAILING */}
            <Route
              path="guest-bots"
              element={
                <PrivateRoute
                  needRole={AppInterface.UserRole.MANAGER}
                  needModule={AppInterface.ModuleName.TG_MAILING}
                  redirectTo={"/login"}
                />
              }
            >
              <Route index element={<Navigate to="bots" />} />
              <Route path="bots" element={<GuestBotsPage />} />
              <Route path="templates" element={<TemplatesPage />} />
              <Route path="bindings" element={<BindingsPage />} />
              <Route path="logs" element={<GuestBotLogsPage />} />
              <Route path="chats" element={<ChatsPage />} />
              <Route
                path="chats/join-jobs/:jobId"
                element={<JoinJobProgressPage />}
              />
              <Route
                path="mailings"
                element={<Navigate to="/telegram/guest-bots/chats" replace />}
              />
              <Route
                path="mailings/:botId/:mailingId"
                element={<MailingProgressPage />}
              />
              <Route path=":id" element={<GuestBotPage />} />
            </Route>

            {postBotRoutes()}

            {/* TGStat - Manager + TGSTAT */}
            <Route
              path="tgstat"
              element={
                <PrivateRoute
                  needRole={AppInterface.UserRole.MANAGER}
                  needModule={AppInterface.ModuleName.TGSTAT}
                  redirectTo={"/login"}
                  element={<LinkComparePage />}
                />
              }
            />
          </Route>

          {/* Marketplace - Manager + MARKETPLACE */}
          <Route
            path="marketplace"
            element={
              <PrivateRoute
                needRole={AppInterface.UserRole.MANAGER}
                needModule={AppInterface.ModuleName.MARKETPLACE}
                redirectTo={"/login"}
                enableComeBackRedirect
              />
            }
          >
            <Route element={<LigaPage />}>
              <Route index element={<Navigate to="products" />} />
              <Route path="products" element={<MarketplaceProductsTable />} />
              <Route
                path="product-categories"
                element={<ProductCategoriesTable />}
              />
              <Route path="cities" element={<MarketplaceCitiesTable />} />
              <Route path="districts" element={<MarketplaceDistrictsTable />} />
              <Route
                path="product-payments"
                element={<ProductPaymentCategorByProduct />}
              />
              <Route path="couriers" element={<StaffTable />} />
              <Route path="questionnaire" element={<QuestionnaireTable />} />
              <Route path="procurements" element={<ProcurementsTable />} />
              <Route path="realization">
                <Route index element={<Navigate to="list" replace />} />
                <Route path="list" element={<RealizationPage />} />
                <Route path="batches" element={<RealizationPage />} />
              </Route>
            </Route>
            <Route path="kraken-analytics" element={<KrakenAnalyticsPage />} />
            <Route
              path="emptytime"
              element={
                <MetabaseEmbed
                  type={Metabase.EmbedType.dashboard}
                  id={14}
                  height="1650px"
                />
              }
            />
            <Route
              path="reloading"
              element={
                <MetabaseEmbed
                  type={Metabase.EmbedType.dashboard}
                  id={6}
                  height="1650px"
                />
              }
            />
            <Route
              path="sales-statistic"
              element={
                <MetabaseEmbed
                  type={Metabase.EmbedType.dashboard}
                  id={20}
                  height="1650px"
                />
              }
            />
            <Route
              path="couriers-statistic"
              element={
                <MetabaseEmbed
                  type={Metabase.EmbedType.dashboard}
                  id={15}
                  height="1650px"
                />
              }
            />
          </Route>

          {/* HR - Manager + HR */}
          <Route
            path="hr"
            element={
              <PrivateRoute
                needRole={AppInterface.UserRole.MANAGER}
                needModule={AppInterface.ModuleName.HR}
                redirectTo={"/login"}
                element={<HrCandidatePage />}
              />
            }
          />

          {/* ADV - Manager + ADV */}
          <Route
            path="adv"
            element={
              <PrivateRoute
                needRole={AppInterface.UserRole.MANAGER}
                needModule={AppInterface.ModuleName.ADV}
                redirectTo={"/login"}
              />
            }
          >
            <Route index element={<Navigate to="orders" />} />
            <Route index path="orders" element={<OrdersPage />} />
            <Route index path="orders/:orderId" element={<OrderPage />} />
            <Route index path="contents" element={<ContentsPage />} />
            <Route path="agents/:agentId/:orderId" element={<OrderPage />} />
            <Route path="agents" element={<AgentsPage />} />
            <Route path="agents/:agentId" element={<AgentPage />} />
            <Route path="top-up" element={<TopUpPage />} />
            <Route path="payments" element={<PaymentsPage />} />
            <Route path="analytics" element={<AnalyticsPage />} />
          </Route>

          {/* OSINT — Sherlok. Manager + sherlok module. */}
          <Route path="osint">
            <Route
              path="sherlok"
              element={
                <PrivateRoute
                  needRole={AppInterface.UserRole.MANAGER}
                  needModule={AppInterface.ModuleName.SHERLOK}
                  redirectTo={"/login"}
                  enableComeBackRedirect
                  element={<SherlokPage />}
                />
              }
            />
          </Route>

          {/* Tracking reads use global userbot RBAC, not account-level ACLs. */}
          <Route
            path="userbot"
            element={
              <PrivateRoute
                needRole={AppInterface.UserRole.MANAGER}
                accessPolicy={AppInterface.AccessPolicy.USERBOT_TRACKING_READ}
              />
            }
          >
            <Route path="join-requests" element={<JoinRequestsPage />} />
            <Route path="join-recovery" element={<JoinRecoveryPage />} />
            <Route path="dialogs" element={<DialogsPage />} />
            <Route path="dialog-passes" element={<DialogPassesPage />} />
            <Route path="scheduled/:id/runs" element={<ScheduledRunsPage />} />
            <Route
              path="membership-clarifications"
              element={<MembershipClarificationsPage />}
            />
          </Route>

          {/* Userbot Chainer — Manager + userbot module */}
          <Route
            path="userbot"
            element={
              <PrivateRoute
                needRole={AppInterface.UserRole.MANAGER}
                needModule={AppInterface.ModuleName.USERBOT}
                redirectTo={"/login"}
                enableComeBackRedirect
              />
            }
          >
            <Route index element={<Navigate to="accounts" replace />} />
            <Route path="proxies" element={<ProxiesPage />} />
            <Route path="roles" element={<RolesPage />} />
            <Route path="accounts" element={<AccountsPage />} />
            <Route
              path="clients"
              element={<Navigate to="/userbot/accounts" replace />}
            />
            <Route path="chains">
              <Route index element={<ChainsPage />} />
              <Route
                path="active"
                element={
                  <Navigate to="/userbot/chains?status=active" replace />
                }
              />
              <Route
                path="finished"
                element={
                  <Navigate to="/userbot/chains?status=finished" replace />
                }
              />
              <Route path="new" element={<ChainBuilderPage />} />
              <Route
                path="finished/:id"
                element={<FinishedChainReportPage />}
              />
              <Route path="report/:id" element={<ChainReportResolverPage />} />
            </Route>
            <Route path="scheduled" element={<ScheduledChainsPage />} />
            <Route path="triggers" element={<TriggersPage />} />
            <Route path="memberships" element={<MembershipsPage />} />
            <Route path="templates">
              <Route index element={<TemplatesGalleryPage />} />
              <Route path="new" element={<TemplateEditorPage />} />
              <Route path="run/:id" element={<TemplateRunPage />} />
              <Route path="edit/:id" element={<TemplateEditorPage />} />
            </Route>
            <Route
              path="ops"
              element={<Navigate to="/userbot/accounts" replace />}
            />
            <Route path="mass-ops" element={<MassOpsPage />} />
            <Route path="batches">
              <Route index element={<BatchesPage />} />
              <Route path=":id" element={<BatchProgressPage />} />
            </Route>
            <Route
              path="profile-dictionaries"
              element={<ProfileDictionariesPage />}
            />
          </Route>

          {/* График присутствия — Manager + staff_schedule module */}
          <Route
            path="staff-schedule"
            element={
              <PrivateRoute
                needRole={AppInterface.UserRole.MANAGER}
                needModule={AppInterface.ModuleName.STAFF_SCHEDULE}
                redirectTo={"/login"}
                enableComeBackRedirect
              />
            }
          >
            <Route index element={<Navigate to="grid" replace />} />
            <Route path="grid" element={<SchedulePage />} />
            <Route path="shift-types" element={<ShiftTypesPage />} />
          </Route>

          <Route
            path="*"
            element={
              <ErrorPage
                code={404}
                text={"Запрашиваемой страницы не существует 🤷‍♂️"}
              />
            }
          />
        </Route>
      </>,
    ),
  )

function App() {
  const themeName = useAppSelector(getAppThemeName)
  const defaultThemeName = useDefaultAppThemeName()
  const theme = useAppTheme(themeName ?? defaultThemeName)

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ru}>
        <ErrorBoundary>
          <OIDCProvider>
            <RouterProvider router={getRouter()} />
          </OIDCProvider>
        </ErrorBoundary>
      </LocalizationProvider>
    </ThemeProvider>
  )
}

export default App
